import React from "react";
import {
  Typography,
  Button,
  Box,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link } from "react-router-dom";
import PricingCalculator from "./PricingCalculator";

const Pricing = () => {
  return (
    <Box
      sx={{
        maxWidth: 1000,
        mx: "auto",
        px: 4,
        py: 8,
      }}
    >
      {/* Pricing Summary */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
          }}
        >
          Simple, predictable slot pricing
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 4,
            color: "text.secondary",
            fontWeight: 400,
            fontSize: { xs: "1rem", sm: "1.1rem" },
          }}
        >
          Pay per client slot — the more you grow, the less you pay per slot.
          Choose any number of clients you want; there are no predefined plans.
          First month is free so you can try out the platform before you pay.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
          sx={{ mb: 4 }}
        >
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ First month is free
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ No setup fee
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ No contracts
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            ✓ Price drops as you scale
          </Typography>
        </Stack>
      </Box>

      {/* Marginal Slot Pricing */}
      <Paper elevation={2} sx={{ p: 4, mb: 6 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
          }}
        >
          Marginal Slot Pricing
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
          Your monthly cost is based on your number of client slots. Each tier
          applies only to the slots in that range.
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
          Choose any number of clients — there are no predefined plans. Scale up
          or down as your business grows.
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Slot Range</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Price Per Slot
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>1–10</TableCell>
                <TableCell align="right">$3.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>11–30</TableCell>
                <TableCell align="right">$2.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>31–60</TableCell>
                <TableCell align="right">$1.50</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>61+</TableCell>
                <TableCell align="right">$1.00</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Stack spacing={1} sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">
              Stripe / Payments Pro Add-On: $10/month flat
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">Not locked into buckets</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">
              Per-slot rate drops automatically as you scale
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <PricingCalculator />

      {/* What's Included */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          What&apos;s included
        </Typography>
        <Stack spacing={1} sx={{ mb: 3, pl: 2 }}>
          <Typography variant="body2">
            • Programming and workout history
          </Typography>
          <Typography variant="body2">
            • Program and template creation
          </Typography>
          <Typography variant="body2">• Custom content uploads</Typography>
          <Typography variant="body2">• Messaging</Typography>
          <Typography variant="body2">• Workout importer</Typography>

          <Typography variant="body2">
            • Platform updates and support
          </Typography>
          <Typography variant="body2">
            • Payments Add-On: $10/month flat (optional)
          </Typography>
          <Typography variant="body2">
            • Client access control when payments stop (with payments add-on)
          </Typography>
        </Stack>
      </Box>

      {/* CTA */}
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          component={Link}
          to="/coaching"
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
          }}
        >
          Start coaching with Trainer App
        </Button>
      </Box>
    </Box>
  );
};

export default Pricing;
